"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { COOKIE_CONSENT_CHANGE_EVENT } from "@/lib/consent/state";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import { funnelText } from "@/lib/funnel-planner/i18n";
import type { AppLocale, FunnelWizardState } from "@/lib/funnel-planner/schema";
import {
  joinAuditTicketGroups,
  STEP_FOUR_AUDIENCE_PLAZA_SCENE_SUPPORT_TICKETS,
  STEP_FOUR_GAME_IFRAME_SUPPORT_TICKETS,
  STEP_FOUR_GAME_PREVIEW_SUPPORT_TICKETS,
  STEP_FOUR_GAME_PREVIEW_SURFACE_TICKETS,
  STEP_FIVE_FUNNEL_DISTRICT_SCENE_SUPPORT_TICKETS,
  STEP_FIVE_GAME_IFRAME_SUPPORT_TICKETS,
  STEP_FIVE_GAME_PREVIEW_SUPPORT_TICKETS,
  STEP_FIVE_GAME_PREVIEW_SURFACE_TICKETS,
  STEP_ONE_GAME_PREVIEW_SUPPORT_TICKETS,
  STEP_ONE_NEXT200_GAME_IFRAME_TICKETS,
  STEP_ONE_NEXT200_GAME_PREVIEW_TICKETS,
  STEP_SIX_BANK_DISTRICT_SCENE_SUPPORT_TICKETS,
  STEP_SIX_GAME_IFRAME_SUPPORT_TICKETS,
  STEP_SIX_GAME_PREVIEW_SUPPORT_TICKETS,
  STEP_SIX_GAME_PREVIEW_SURFACE_TICKETS,
  STEP_SEVEN_FINALE_CITY_SCENE_SUPPORT_TICKETS,
  STEP_SEVEN_GAME_IFRAME_SUPPORT_TICKETS,
  STEP_SEVEN_GAME_PREVIEW_SUPPORT_TICKETS,
  STEP_SEVEN_GAME_PREVIEW_SURFACE_TICKETS,
  STEP_TWO_GAME_PREVIEW_SUPPORT_TICKETS,
  STEP_TWO_GAME_PREVIEW_SURFACE_TICKETS,
  STEP_THREE_GAME_IFRAME_SUPPORT_TICKETS,
  STEP_THREE_GAME_PREVIEW_SUPPORT_TICKETS,
  STEP_THREE_GAME_PREVIEW_SURFACE_TICKETS,
  STEP_THREE_PRODUCT_AVENUE_SCENE_SUPPORT_TICKETS,
} from "@/lib/funnel-planner/gamification-next200-step1-tickets";

const GAME_SRC = "/funnel-planner-game/companion-world-v3.html?embed=1";
const LARGE_SHELL_QUERY = "(min-width: 2560px)";
const GAME_VISIBILITY_STORAGE_KEY = "seoh:funnel-planner-game-visible-v3";
const GAME_DISABLED_SHELLS = new Set(["mobile", "ereader"]);

function hasPreferenceConsent(): boolean {
  return window.__seohConsent?.has("preferences") === true;
}
const STEP_ONE_PREVIEW_OFFER_CONFIGURATOR_TICKET =
  "P3-001002-interactive-step-01-interactive-game-preview-planner-offer-assembly-configurator";
const STEP_ONE_GAME_PREVIEW_OFFER_CONFIGURATOR_TICKET =
  "P3-101002-interactive-step-01-game-preview-offer-assembly-configurator";
const PREVIEW_AUDIT_TICKETS_BY_STEP: Partial<
  Record<FunnelWizardState["step"], string>
> = {
  1: joinAuditTicketGroups(
    [
      "P1-001001-interactive-step-01-interactive-game-preview-planner-readiness-board",
      "P1-101001-interactive-step-01-game-preview-readiness-board",
      STEP_ONE_PREVIEW_OFFER_CONFIGURATOR_TICKET,
      STEP_ONE_GAME_PREVIEW_OFFER_CONFIGURATOR_TICKET,
    ],
    STEP_ONE_NEXT200_GAME_PREVIEW_TICKETS,
    STEP_ONE_NEXT200_GAME_IFRAME_TICKETS,
    STEP_ONE_GAME_PREVIEW_SUPPORT_TICKETS,
  ),
  2: joinAuditTicketGroups(
    [
      "P1-108001-interactive-step-02-interactive-game-preview-planner-readiness-board",
      "P1-207001-interactive-step-02-game-preview-readiness-board",
    ].join(";"),
    STEP_TWO_GAME_PREVIEW_SUPPORT_TICKETS,
    STEP_TWO_GAME_PREVIEW_SURFACE_TICKETS,
  ),
  3: joinAuditTicketGroups(
    [
      "P1-215001-interactive-step-03-interactive-game-preview-planner-readiness-board",
      "P1-307001-interactive-step-03-game-preview-readiness-board",
      "P1-313001-interactive-step-03-product-avenue-scene-readiness-board",
    ].join(";"),
    STEP_THREE_GAME_PREVIEW_SUPPORT_TICKETS,
    STEP_THREE_GAME_IFRAME_SUPPORT_TICKETS,
    STEP_THREE_GAME_PREVIEW_SURFACE_TICKETS,
    STEP_THREE_PRODUCT_AVENUE_SCENE_SUPPORT_TICKETS,
  ),
  4: joinAuditTicketGroups(
    [
      "P1-315001-interactive-step-04-interactive-game-preview-planner-readiness-board",
      "P1-413001-interactive-step-04-game-preview-readiness-board",
      "P1-419001-interactive-step-04-audience-plaza-scene-readiness-board",
    ].join(";"),
    STEP_FOUR_GAME_PREVIEW_SUPPORT_TICKETS,
    STEP_FOUR_GAME_IFRAME_SUPPORT_TICKETS,
    STEP_FOUR_GAME_PREVIEW_SURFACE_TICKETS,
    STEP_FOUR_AUDIENCE_PLAZA_SCENE_SUPPORT_TICKETS,
  ),
  5: joinAuditTicketGroups(
    [
      "P1-421001-interactive-step-05-interactive-game-preview-planner-readiness-board",
      "P1-516001-interactive-step-05-game-preview-readiness-board",
      "P1-522001-interactive-step-05-funnel-district-scene-readiness-board",
    ].join(";"),
    STEP_FIVE_GAME_PREVIEW_SUPPORT_TICKETS,
    STEP_FIVE_GAME_IFRAME_SUPPORT_TICKETS,
    STEP_FIVE_GAME_PREVIEW_SURFACE_TICKETS,
    STEP_FIVE_FUNNEL_DISTRICT_SCENE_SUPPORT_TICKETS,
  ),
  6: joinAuditTicketGroups(
    [
      "P1-524001-interactive-step-06-interactive-game-preview-planner-readiness-board",
      "P1-649001-interactive-step-06-game-preview-readiness-board",
      "P1-655001-interactive-step-06-bank-district-scene-readiness-board",
    ].join(";"),
    STEP_SIX_GAME_PREVIEW_SUPPORT_TICKETS,
    STEP_SIX_GAME_IFRAME_SUPPORT_TICKETS,
    STEP_SIX_GAME_PREVIEW_SURFACE_TICKETS,
    STEP_SIX_BANK_DISTRICT_SCENE_SUPPORT_TICKETS,
  ),
  7: joinAuditTicketGroups(
    [
      "P1-657001-interactive-step-07-interactive-game-preview-planner-readiness-board",
      "P1-750001-interactive-step-07-game-preview-readiness-board",
      "P1-756001-interactive-step-07-finale-city-scene-readiness-board",
      "P3-657002-interactive-step-07-interactive-game-preview-planner-offer-assembly-configurator",
      "P3-657003-interactive-step-07-interactive-game-preview-planner-citation-path-trace",
      "P3-657004-interactive-step-07-interactive-game-preview-planner-find-the-gap-page-inspection",
      "P3-657005-interactive-step-07-interactive-game-preview-planner-interactive-audit-map",
      "P3-750002-interactive-step-07-game-preview-offer-assembly-configurator",
      "P3-750003-interactive-step-07-game-preview-citation-path-trace",
      "P3-750004-interactive-step-07-game-preview-find-the-gap-page-inspection",
      "P3-750005-interactive-step-07-game-preview-interactive-audit-map",
    ].join(";"),
    STEP_SEVEN_GAME_PREVIEW_SUPPORT_TICKETS,
    STEP_SEVEN_GAME_IFRAME_SUPPORT_TICKETS,
    STEP_SEVEN_GAME_PREVIEW_SURFACE_TICKETS,
    STEP_SEVEN_FINALE_CITY_SCENE_SUPPORT_TICKETS,
  ),
};

type PreviewReadinessStatus = "untouched" | "review" | "ready";

type PreviewReadinessItemInput = {
  readonly id: string;
  readonly label: string;
  readonly ready: boolean;
  readonly step: FunnelWizardState["step"];
};

type PreviewReadinessItem = PreviewReadinessItemInput & {
  readonly status: PreviewReadinessStatus;
};

type PreviewReadinessLabels = {
  readonly title: string;
  readonly body: string;
  readonly progress: string;
  readonly tierLabel: string;
  readonly blockersLabel: string;
  readonly noBlockers: string;
  readonly statusSelect: string;
  readonly statusUntouched: string;
  readonly statusReview: string;
  readonly statusReady: string;
  readonly liveSummary: string;
  readonly itemTemplate: string;
  readonly itemTraffic: string;
  readonly itemOffer: string;
  readonly itemAudience: string;
  readonly itemStages: string;
  readonly itemProjection: string;
  readonly itemHandoff: string;
  readonly tierDraft: string;
  readonly tierDraftPlus: string;
  readonly tierReviewReady: string;
  readonly tierHandoffReady: string;
};

type GamePreviewLabels = {
  readonly hiddenAria: string;
  readonly hiddenBody: string;
  readonly show: string;
  readonly liveAria: string;
  readonly toolbar: string;
  readonly hide: string;
};

const GAME_PREVIEW_LABELS: Record<AppLocale, GamePreviewLabels> = {
  en: {
    hiddenAria: "Hidden 3D funnel planner city preview",
    hiddenBody: "3D city preview is hidden for this browser.",
    show: "Show 3D city",
    liveAria: "Live funnel planner city preview",
    toolbar: "3D city preview",
    hide: "Hide",
  },
  ru: {
    hiddenAria: "Скрытый 3D-просмотр города планировщика воронки",
    hiddenBody: "3D-просмотр города скрыт для этого браузера.",
    show: "Показать 3D-город",
    liveAria: "Живой 3D-просмотр города планировщика воронки",
    toolbar: "3D-просмотр города",
    hide: "Скрыть",
  },
  ar: {
    hiddenAria: "معاينة مدينة مخطط القمع ثلاثية الأبعاد مخفية",
    hiddenBody: "تم إخفاء معاينة المدينة ثلاثية الأبعاد لهذا المتصفح.",
    show: "إظهار المدينة ثلاثية الأبعاد",
    liveAria: "معاينة مباشرة لمدينة مخطط القمع ثلاثية الأبعاد",
    toolbar: "معاينة المدينة ثلاثية الأبعاد",
    hide: "إخفاء",
  },
  es: {
    hiddenAria:
      "Vista previa 3D oculta de la ciudad del planificador de embudos",
    hiddenBody:
      "La vista previa 3D de la ciudad está oculta para este navegador.",
    show: "Mostrar ciudad 3D",
    liveAria:
      "Vista previa 3D en vivo de la ciudad del planificador de embudos",
    toolbar: "Vista previa 3D",
    hide: "Ocultar",
  },
  fr: {
    hiddenAria: "Aperçu 3D masqué de la ville du planificateur d'entonnoir",
    hiddenBody: "L'aperçu 3D de la ville est masqué pour ce navigateur.",
    show: "Afficher la ville 3D",
    liveAria: "Aperçu 3D en direct de la ville du planificateur d'entonnoir",
    toolbar: "Aperçu 3D de la ville",
    hide: "Masquer",
  },
  he: {
    hiddenAria: "תצוגת עיר תלת-ממדית מוסתרת של מתכנן המשפך",
    hiddenBody: "תצוגת העיר התלת-ממדית מוסתרת בדפדפן הזה.",
    show: "הצג עיר תלת-ממדית",
    liveAria: "תצוגת עיר תלת-ממדית חיה של מתכנן המשפך",
    toolbar: "תצוגת עיר תלת-ממדית",
    hide: "הסתר",
  },
  de: {
    hiddenAria: "Ausgeblendete 3D-Stadtvorschau des Funnel-Planners",
    hiddenBody: "Die 3D-Stadtvorschau ist für diesen Browser ausgeblendet.",
    show: "3D-Stadt anzeigen",
    liveAria: "Live-3D-Stadtvorschau des Funnel-Planners",
    toolbar: "3D-Stadtvorschau",
    hide: "Ausblenden",
  },
  "pt-br": {
    hiddenAria: "Prévia 3D oculta da cidade do planejador de funil",
    hiddenBody: "A prévia 3D da cidade está oculta neste navegador.",
    show: "Mostrar cidade 3D",
    liveAria: "Prévia 3D ao vivo da cidade do planejador de funil",
    toolbar: "Prévia 3D da cidade",
    hide: "Ocultar",
  },
  ja: {
    hiddenAria: "ファネルプランナーの3D都市プレビューは非表示です",
    hiddenBody: "このブラウザーでは3D都市プレビューが非表示になっています。",
    show: "3D都市を表示",
    liveAria: "ファネルプランナーのライブ3D都市プレビュー",
    toolbar: "3D都市プレビュー",
    hide: "非表示",
  },
  hi: {
    hiddenAria: "फ़नल प्लानर शहर का छिपा हुआ 3D पूर्वावलोकन",
    hiddenBody: "इस ब्राउज़र के लिए 3D शहर पूर्वावलोकन छिपा हुआ है।",
    show: "3D शहर दिखाएँ",
    liveAria: "फ़नल प्लानर शहर का लाइव 3D पूर्वावलोकन",
    toolbar: "3D शहर पूर्वावलोकन",
    hide: "छिपाएँ",
  },
  id: {
    hiddenAria: "Pratinjau kota 3D perencana funnel disembunyikan",
    hiddenBody: "Pratinjau kota 3D disembunyikan untuk browser ini.",
    show: "Tampilkan kota 3D",
    liveAria: "Pratinjau kota 3D langsung perencana funnel",
    toolbar: "Pratinjau kota 3D",
    hide: "Sembunyikan",
  },
  "zh-cn": {
    hiddenAria: "隐藏的漏斗规划器 3D 城市预览",
    hiddenBody: "此浏览器已隐藏 3D 城市预览。",
    show: "显示 3D 城市",
    liveAria: "漏斗规划器实时 3D 城市预览",
    toolbar: "3D 城市预览",
    hide: "隐藏",
  },
  tr: {
    hiddenAria: "Gizli 3D funnel planlayıcı şehir önizlemesi",
    hiddenBody: "Bu tarayıcı için 3D şehir önizlemesi gizlendi.",
    show: "3D şehri göster",
    liveAria: "Canlı 3D funnel planlayıcı şehir önizlemesi",
    toolbar: "3D şehir önizlemesi",
    hide: "Gizle",
  },
  ko: {
    hiddenAria: "숨겨진 퍼널 플래너 3D 도시 미리보기",
    hiddenBody: "이 브라우저에서는 3D 도시 미리보기가 숨겨져 있습니다.",
    show: "3D 도시 보기",
    liveAria: "퍼널 플래너 실시간 3D 도시 미리보기",
    toolbar: "3D 도시 미리보기",
    hide: "숨기기",
  },
  it: {
    hiddenAria: "Anteprima 3D nascosta della città del pianificatore funnel",
    hiddenBody: "L'anteprima 3D della città è nascosta per questo browser.",
    show: "Mostra città 3D",
    liveAria: "Anteprima 3D live della città del pianificatore funnel",
    toolbar: "Anteprima città 3D",
    hide: "Nascondi",
  },
};

const DEFAULT_STEP_ONE_READINESS_LABELS: PreviewReadinessLabels = {
  title: "Planner readiness board",
  body: "Inputs, handoff, and projection checks for the current planner draft.",
  progress: "{ready} of {total} ready",
  tierLabel: "Readiness tier",
  blockersLabel: "Fix next",
  noBlockers: "No blockers marked.",
  statusSelect: "{item} status",
  statusUntouched: "Untouched",
  statusReview: "In review",
  statusReady: "Ready",
  liveSummary: "{ready} of {total} checks ready. Tier: {tier}. Next: {next}.",
  itemTemplate: "Funnel type selected",
  itemTraffic: "Traffic inputs mapped",
  itemOffer: "Offer stack defined",
  itemAudience: "Audience and goal captured",
  itemStages: "Funnel stages mapped",
  itemProjection: "Projection reviewed",
  itemHandoff: "Handoff ready",
  tierDraft: "Draft",
  tierDraftPlus: "Draft plus",
  tierReviewReady: "Review ready",
  tierHandoffReady: "Handoff ready",
};

const PAID_ACQUISITION_TYPES = new Set([
  "cpc",
  "cpm",
  "cpv",
  "flat_fee",
  "cpa",
  "cost_per_lead",
]);
const PARTNER_ACQUISITION_TYPES = new Set(["revenue_share"]);

function templateLabel(
  template: string,
  values: Record<string, string | number>,
): string {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, String(value)),
    template,
  );
}

function localizedPreviewReadinessLabels(
  content: FunnelPlannerContent,
): PreviewReadinessLabels {
  const d = DEFAULT_STEP_ONE_READINESS_LABELS;
  return {
    title: funnelText(content, "gamificationPreviewReadinessTitle", d.title),
    body: funnelText(content, "gamificationPreviewReadinessBody", d.body),
    progress: funnelText(
      content,
      "gamificationPreviewReadinessProgress",
      d.progress,
    ),
    tierLabel: funnelText(
      content,
      "gamificationPreviewReadinessTierLabel",
      d.tierLabel,
    ),
    blockersLabel: funnelText(
      content,
      "gamificationPreviewReadinessBlockersLabel",
      d.blockersLabel,
    ),
    noBlockers: funnelText(
      content,
      "gamificationPreviewReadinessNoBlockers",
      d.noBlockers,
    ),
    statusSelect: funnelText(
      content,
      "gamificationPreviewReadinessStatusSelect",
      d.statusSelect,
    ),
    statusUntouched: funnelText(
      content,
      "gamificationPreviewReadinessStatusUntouched",
      d.statusUntouched,
    ),
    statusReview: funnelText(
      content,
      "gamificationPreviewReadinessStatusReview",
      d.statusReview,
    ),
    statusReady: funnelText(content, "gamificationStatusReady", d.statusReady),
    liveSummary: funnelText(
      content,
      "gamificationPreviewReadinessLiveSummary",
      d.liveSummary,
    ),
    itemTemplate: funnelText(
      content,
      "gamificationReadinessTemplateLabel",
      d.itemTemplate,
    ),
    itemTraffic: funnelText(
      content,
      "gamificationReadinessTrafficLabel",
      d.itemTraffic,
    ),
    itemOffer: funnelText(
      content,
      "gamificationReadinessOfferLabel",
      d.itemOffer,
    ),
    itemAudience: funnelText(
      content,
      "gamificationReadinessAudienceLabel",
      d.itemAudience,
    ),
    itemStages: funnelText(
      content,
      "gamificationReadinessStagesLabel",
      d.itemStages,
    ),
    itemProjection: funnelText(
      content,
      "gamificationReadinessProjectionLabel",
      d.itemProjection,
    ),
    itemHandoff: funnelText(
      content,
      "gamificationReadinessHandoffLabel",
      d.itemHandoff,
    ),
    tierDraft: funnelText(
      content,
      "gamificationPreviewReadinessTierDraft",
      d.tierDraft,
    ),
    tierDraftPlus: funnelText(
      content,
      "gamificationPreviewReadinessTierDraftPlus",
      d.tierDraftPlus,
    ),
    tierReviewReady: funnelText(
      content,
      "gamificationPreviewReadinessTierReviewReady",
      d.tierReviewReady,
    ),
    tierHandoffReady: funnelText(
      content,
      "gamificationPreviewReadinessTierHandoffReady",
      d.tierHandoffReady,
    ),
  };
}

function effectiveStageCount(state: FunnelWizardState): number {
  const graphStageCount =
    state.graph?.nodes.filter((node) => node.type === "stage").length ?? 0;
  return graphStageCount > 0 ? graphStageCount : state.stages.length;
}

function defaultPreviewReadinessStatus(
  ready: boolean,
  step: FunnelWizardState["step"],
  activeStep: FunnelWizardState["step"],
): PreviewReadinessStatus {
  if (ready) return "ready";
  if (activeStep >= step) return "review";
  return "untouched";
}

function buildPreviewReadinessItems(
  state: FunnelWizardState,
  labels: PreviewReadinessLabels,
): readonly PreviewReadinessItemInput[] {
  const hasTraffic = state.trafficChannels.some(
    (channel) => channel.monthlyVisitors > 0,
  );
  const hasAudienceGoal =
    state.audience !== null && state.goal.trim().length > 0;
  const hasProjectionReview = state.step > 6 || state.completedAt !== null;
  const hasHandoff =
    state.completedAt !== null || state.contact?.privacyNoticeAccepted === true;

  return [
    {
      id: "template",
      label: labels.itemTemplate,
      ready: state.templateId !== null,
      step: 1,
    },
    {
      id: "traffic",
      label: labels.itemTraffic,
      ready: hasTraffic,
      step: 2,
    },
    {
      id: "offer",
      label: labels.itemOffer,
      ready: state.products.length > 0,
      step: 3,
    },
    {
      id: "audience",
      label: labels.itemAudience,
      ready: hasAudienceGoal,
      step: 4,
    },
    {
      id: "stages",
      label: labels.itemStages,
      ready: effectiveStageCount(state) > 0,
      step: 5,
    },
    {
      id: "projection",
      label: labels.itemProjection,
      ready: hasProjectionReview,
      step: 6,
    },
    {
      id: "handoff",
      label: labels.itemHandoff,
      ready: hasHandoff,
      step: 7,
    },
  ];
}

function previewReadinessTier(
  readyCount: number,
  totalCount: number,
  labels: PreviewReadinessLabels,
): string {
  if (readyCount >= totalCount) return labels.tierHandoffReady;
  if (readyCount >= 5) return labels.tierReviewReady;
  if (readyCount >= 3) return labels.tierDraftPlus;
  return labels.tierDraft;
}

function previewReadinessTierKey(
  readyCount: number,
  totalCount: number,
): string {
  if (readyCount >= totalCount) return "handoff_ready";
  if (readyCount >= 5) return "review_ready";
  if (readyCount >= 3) return "draft_plus";
  return "draft";
}

function gameAcquisitionType(
  acquisitionType: string,
): "organic" | "paid" | "partner" | "owned" | "offline" {
  if (PAID_ACQUISITION_TYPES.has(acquisitionType)) return "paid";
  if (PARTNER_ACQUISITION_TYPES.has(acquisitionType)) return "partner";
  if (acquisitionType === "labor_time") return "owned";
  return "organic";
}

function gameCostModel(acquisitionType: string): string {
  switch (acquisitionType) {
    case "cpc":
    case "cpm":
    case "cpv":
    case "cpa":
      return acquisitionType;
    case "cost_per_lead":
      return "cpl";
    case "flat_fee":
      return "flat";
    case "revenue_share":
      return "revshare";
    case "labor_time":
      return "labor";
    default:
      return "none";
  }
}

function channelCostValue(
  channel: FunnelWizardState["trafficChannels"][number],
): number {
  return (
    channel.cpc ??
    channel.costPerView ??
    channel.cpm ??
    channel.flatMonthlyFee ??
    channel.costPerLead ??
    channel.costPerAcquisition ??
    channel.revenueSharePercent ??
    channel.hourlyCost ??
    0
  );
}

function gameProductType(productType: string): string {
  switch (productType) {
    case "saas":
    case "software_plugin":
    case "seat_based_saas":
    case "usage_based_api":
    case "ai_agent":
    case "mobile_app":
      return "saas";
    case "online_course":
    case "cohort_course":
      return "course";
    case "coaching":
    case "mentorship":
    case "high_ticket":
      return "coaching";
    case "membership":
    case "paid_community":
    case "paid_newsletter":
      return "membership";
    case "digital_download":
      return "template";
    case "physical_product":
    case "supplement":
    case "subscription_box":
      return "physical";
    case "agency_retainer":
    case "done_for_you":
    case "local_service":
    case "franchise":
      return "agency_service";
    case "affiliate":
      return "affiliate";
    case "event":
      return "event";
    default:
      return "custom";
  }
}

function gamePriceType(priceType: string, productType: string): string {
  if (
    priceType === "monthly" ||
    priceType === "yearly" ||
    productType === "membership"
  )
    return "subscription";
  if (priceType === "usage_based" || productType === "usage_based_api")
    return "usage-based";
  if (priceType === "seat_based") return "subscription";
  if (priceType === "payment_plan") return "payment-plan";
  return "one-time";
}

function gameCheckoutType(checkoutType?: string): string {
  switch (checkoutType) {
    case "sales_call_close":
      return "sales-call";
    case "invoice":
      return "invoice";
    case "subscription_checkout":
    case "two_step":
    case "one_step":
      return "hosted";
    default:
      return "hosted";
  }
}

function gameOfferRole(offerRole?: string): string {
  switch (offerRole) {
    case "order_bump":
      return "bump";
    case "continuity":
      return "retention";
    case "application_only":
    case "quote_only":
    case "backend":
      return "core";
    default:
      return offerRole ?? "core";
  }
}

function gameTrackingSetup(trackingSetup?: string): string {
  switch (trackingSetup) {
    case "server_side":
      return "server";
    case "crm_matched":
      return "crm";
    case "utm_only":
      return "utm";
    default:
      return trackingSetup ?? "utm";
  }
}

function gameStageTracking(trackingStatus?: string): string {
  switch (trackingStatus) {
    case "installed":
    case "server_side":
    case "crm_matched":
      return "installed";
    case "verified":
      return "verified";
    case "planned":
    case "utm_only":
    case "pixel":
      return "planned";
    default:
      return "none";
  }
}

function gameStageStatus(
  publishedStatus?: string,
  assetStatus?: string,
): string {
  if (publishedStatus === "live" || assetStatus === "live") return "live";
  if (publishedStatus === "paused" || assetStatus === "paused") return "paused";
  if (assetStatus === "needs_fix") return "needs_asset";
  if (assetStatus === "draft" || publishedStatus === "draft") return "draft";
  return "ready";
}

function gameOwner(owner?: string): string {
  const value = (owner ?? "").toLowerCase();
  if (
    ["growth", "sales", "content", "ops", "founder", "agency"].includes(value)
  )
    return value;
  if (value.includes("sale")) return "sales";
  if (value.includes("content") || value.includes("seo")) return "content";
  if (value.includes("founder") || value.includes("owner")) return "founder";
  if (value.includes("ops") || value.includes("operation")) return "ops";
  return "growth";
}

function buildGamePayload(state: FunnelWizardState) {
  const activeScenario = state.scenarios[0];
  const regional = state.regionalSettings;
  const details = state.audienceGoalDetails;

  return {
    step: Math.max(0, Math.min(6, state.step - 1)),
    templateId: state.templateId,
    audience: state.audience,
    regionalSettings: regional,
    audienceGoalDetails: details,
    trafficChannels: state.trafficChannels
      .slice(0, 12)
      .map((channel, index) => ({
        id: channel.id,
        label: channel.label,
        monthlyVisitors: channel.monthlyVisitors,
        acquisitionType: gameAcquisitionType(channel.acquisitionType),
        costModel: gameCostModel(channel.acquisitionType),
        costValue: channelCostValue(channel),
        intent:
          channel.intentLevel === "hot"
            ? "conversion"
            : channel.intentLevel === "warm"
              ? "consideration"
              : "awareness",
        funnelPosition:
          channel.funnelPosition === "retargeting"
            ? "post_purchase"
            : (channel.funnelPosition ?? (index < 2 ? "top" : "middle")),
        trackingSetup: gameTrackingSetup(channel.trackingSetup),
        trackingConfidence: channel.trackingConfidencePct ?? 65,
        audienceTemp:
          channel.audienceTemperature ?? channel.intentLevel ?? "cold",
        geoTarget: channel.geo ?? regional.countryCode,
        deviceMix: channel.device ?? "all",
        language: channel.language ?? regional.locale,
        budget: channel.monthlyBudgetCap ?? 0,
        rampDays:
          channel.rampType === "launch_spike"
            ? 14
            : channel.rampType === "linear"
              ? 45
              : 30,
      })),
    products: state.products.slice(0, 30).map((product) => ({
      name: product.name,
      productType: gameProductType(product.productType),
      priceType: gamePriceType(product.priceType, product.productType),
      price: product.price,
      cogs: product.cogsPercent / 100,
      refundRate: product.refundRatePercent,
      merchantFee: product.merchantFeePercent,
      churnRate: product.churnRatePercent ?? 0,
      trialDays: product.hasTrial ? (product.trialDays ?? 14) : 0,
      paymentPlan: product.paymentPlanMonths ?? 1,
      upsellTake:
        product.upsellTakeRatePct ?? product.orderBumpTakeRatePct ?? 10,
      checkoutType: gameCheckoutType(product.checkoutType),
      guarantee: product.guaranteeType,
      offerRole: gameOfferRole(product.offerRole),
    })),
    stages: state.stages.slice(0, 8).map((stage) => ({
      name: stage.name,
      stageType: stage.stageType,
      conversionRate: stage.conversionRate,
      status: gameStageStatus(stage.publishedStatus, stage.assetStatus),
      tracking: gameStageTracking(stage.trackingStatus),
      owner: gameOwner(stage.owner),
      priority: stage.priority ?? "medium",
      waitDays: stage.waitDays ?? 0,
    })),
    projectionInputs: state.projectionInputs,
    scenario: activeScenario?.visitorMultiplier ?? 1,
    revenueMultiplier: activeScenario?.revenueMultiplier ?? 1,
    expenses: state.expenses.slice(0, 8).map((expense) => ({
      category: expense.category,
      amount: expense.amount,
      frequency: expense.frequency,
    })),
    capacityLeads:
      state.capacityConfig?.maxCallsPerMonth ??
      state.capacityConfig?.salesRepCount ??
      60,
    contact: state.contact,
  };
}

export type InitialGamePreview = {
  eligible: boolean;
  visible: boolean;
  disabled: boolean;
};

export function FunnelPlannerGamePreview({
  content,
  locale,
  state,
  initialPreview,
}: {
  readonly content: FunnelPlannerContent;
  readonly locale: AppLocale;
  readonly state: FunnelWizardState;
  readonly initialPreview?: InitialGamePreview;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isGameEligibleShell, setIsGameEligibleShell] = useState(initialPreview?.eligible ?? false);
  const [isGameVisible, setIsGameVisible] = useState(initialPreview?.visible ?? false);
  const labels = GAME_PREVIEW_LABELS[locale] ?? GAME_PREVIEW_LABELS.en;
  const isNoGameQaMode =
    initialPreview?.disabled ||
    (typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("qa_no_game") === "1");
  const readinessLabels = useMemo(
    () => localizedPreviewReadinessLabels(content),
    [content],
  );
  const previewReadinessAuditTickets =
    locale === "en" ? PREVIEW_AUDIT_TICKETS_BY_STEP[state.step] : undefined;
  const shouldShowPreviewReadiness = Boolean(previewReadinessAuditTickets);
  const previewReadinessItems = useMemo(
    () => buildPreviewReadinessItems(state, readinessLabels),
    [readinessLabels, state],
  );
  const resolvedReadinessItems: readonly PreviewReadinessItem[] = useMemo(
    () =>
      previewReadinessItems.map((item) => ({
        ...item,
        status: defaultPreviewReadinessStatus(
          item.ready,
          item.step,
          state.step,
        ),
      })),
    [previewReadinessItems, state.step],
  );
  const readyCount = resolvedReadinessItems.filter(
    (item) => item.status === "ready",
  ).length;
  const readinessTier = previewReadinessTier(
    readyCount,
    resolvedReadinessItems.length,
    readinessLabels,
  );
  const blockerItems = resolvedReadinessItems.filter(
    (item) => item.status !== "ready",
  );
  const readinessTierKey = previewReadinessTierKey(
    readyCount,
    resolvedReadinessItems.length,
  );
  const nextBlocker = blockerItems[0]?.label ?? readinessLabels.noBlockers;
  const readinessLiveSummary = templateLabel(readinessLabels.liveSummary, {
    ready: readyCount,
    total: resolvedReadinessItems.length,
    tier: readinessTier,
    next: nextBlocker,
  });

  useEffect(() => {
    const largeMedia = window.matchMedia(LARGE_SHELL_QUERY);
    const update = () => {
      const override = new URLSearchParams(window.location.search).get("game");
      if (
        override === "0" ||
        override === "false" ||
        override === "1" ||
        override === "true"
      ) {
        setIsGameEligibleShell(true);
        return;
      }
      const bodyShell = document.body.getAttribute("data-device-shell");
      const nextLargeShell = bodyShell === "4k" || largeMedia.matches;
      setIsGameEligibleShell(
        bodyShell ? !GAME_DISABLED_SHELLS.has(bodyShell) : nextLargeShell,
      );
    };
    update();
    largeMedia.addEventListener("change", update);
    return () => {
      largeMedia.removeEventListener("change", update);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const override = params.get("game");
    const storedVisibility = hasPreferenceConsent()
      ? window.localStorage.getItem(GAME_VISIBILITY_STORAGE_KEY)
      : null;
    let nextVisible =
      storedVisibility === null
        ? isGameEligibleShell
        : storedVisibility !== "0";
    if (override === "0" || override === "false") {
      nextVisible = false;
    } else if (override === "1" || override === "true") {
      nextVisible = true;
    }
    const handle = window.setTimeout(() => setIsGameVisible(nextVisible), 0);
    return () => window.clearTimeout(handle);
  }, [isGameEligibleShell]);

  useEffect(() => {
    const handleConsentChange = () => {
      if (hasPreferenceConsent()) return;
      window.localStorage.removeItem(GAME_VISIBILITY_STORAGE_KEY);
      setIsGameVisible(isGameEligibleShell);
    };

    window.addEventListener(COOKIE_CONSENT_CHANGE_EVENT, handleConsentChange);
    return () =>
      window.removeEventListener(
        COOKIE_CONSENT_CHANGE_EVENT,
        handleConsentChange,
      );
  }, [isGameEligibleShell]);

  const toggleGameVisibility = useCallback(() => {
    setIsGameVisible((current) => {
      const next = !current;
      if (hasPreferenceConsent()) {
        window.localStorage.setItem(
          GAME_VISIBILITY_STORAGE_KEY,
          next ? "1" : "0",
        );
      }
      return next;
    });
  }, []);

  const postPlannerState = useCallback(() => {
    const frame = iframeRef.current?.contentWindow;
    if (!frame) return;
    frame.postMessage(
      {
        type: "seoh:funnel-planner-state",
        version: 1,
        payload: buildGamePayload(state),
      },
      window.location.origin,
    );
  }, [state]);

  useEffect(() => {
    if (!isGameEligibleShell || !isGameVisible) return;
    postPlannerState();
  }, [isGameEligibleShell, isGameVisible, postPlannerState]);

  if (isNoGameQaMode) {
    return null;
  }

  if (!isGameEligibleShell) {
    return null;
  }

  if (!isGameVisible) {
    return (
      <aside
        data-funnel-planner-game-preview
        data-audit-ticket={previewReadinessAuditTickets}
        data-native-experience-pipeline="EXP-08 EXP-11 EXP-12"
        data-readiness-tier={
          shouldShowPreviewReadiness ? readinessTier : undefined
        }
        data-readiness-tier-key={
          shouldShowPreviewReadiness ? readinessTierKey : undefined
        }
        data-readiness-ready-count={
          shouldShowPreviewReadiness ? readyCount : undefined
        }
        data-readiness-total-count={
          shouldShowPreviewReadiness ? resolvedReadinessItems.length : undefined
        }
        data-tier-one-family={
          shouldShowPreviewReadiness ? "readiness_board" : undefined
        }
        aria-label={labels.hiddenAria}
        className="funnel-planner-game-preview funnel-planner-game-preview--hidden"
      >
        <span className="sr-only" role="status" aria-live="polite">
          {readinessLiveSummary}
        </span>
        <button type="button" onClick={toggleGameVisibility}>
          {labels.show}
        </button>
        <style>{`
          [data-funnel-planner-shell="wizard"] {
            max-width: min(94vw, 176rem) !important;
          }

          [data-funnel-planner-game-grid="true"] {
            display: block;
          }

          @media (max-width: 1279px) {
            [data-funnel-planner-game-grid="true"] {
              grid-template-columns: minmax(0, 1fr);
            }
          }

          [data-funnel-planner-control-column="true"] {
            min-width: 0;
          }

          .funnel-planner-game-preview--hidden {
            position: fixed;
            inset-inline-end: clamp(0.75rem, 2vw, 1.5rem);
            bottom: clamp(0.75rem, 2vw, 1.5rem);
            z-index: 30;
            width: auto;
            height: auto;
            min-width: 0;
            border: 0;
            border-radius: 999px;
            overflow: visible;
            background: transparent;
            box-shadow: none;
          }

          .funnel-planner-game-preview--hidden button {
            min-height: 2.35rem;
            border: 1px solid rgba(96, 165, 250, 0.45);
            border-radius: 999px;
            background: rgba(15, 23, 42, 0.92);
            box-shadow: 0 1rem 2.5rem rgba(0, 0, 0, 0.32);
            color: #dbeafe;
            font-size: 0.82rem;
            font-weight: 700;
            padding: 0.45rem 0.85rem;
            cursor: pointer;
          }

          @media (max-width: 1279px) {
            .funnel-planner-game-preview--hidden {
              inset-inline-end: 0.75rem;
              bottom: 0.75rem;
            }
          }
        `}</style>
      </aside>
    );
  }

  return (
    <aside
      data-funnel-planner-game-preview
      data-audit-ticket={previewReadinessAuditTickets}
      data-readiness-tier={
        shouldShowPreviewReadiness ? readinessTier : undefined
      }
      data-readiness-tier-key={
        shouldShowPreviewReadiness ? readinessTierKey : undefined
      }
      data-readiness-ready-count={
        shouldShowPreviewReadiness ? readyCount : undefined
      }
      data-readiness-total-count={
        shouldShowPreviewReadiness ? resolvedReadinessItems.length : undefined
      }
      data-tier-one-family={
        shouldShowPreviewReadiness ? "readiness_board" : undefined
      }
      data-native-experience-pipeline="EXP-08 EXP-12"
      aria-label={labels.liveAria}
      className="funnel-planner-game-preview"
    >
      <div className="funnel-planner-game-preview__toolbar">
        <span>{labels.toolbar}</span>
        <button type="button" onClick={toggleGameVisibility}>
          {labels.hide}
        </button>
      </div>
      <iframe
        ref={iframeRef}
        src={GAME_SRC}
        title={labels.liveAria}
        loading="eager"
        onLoad={postPlannerState}
        allow="fullscreen"
      />
      <style>{`
        [data-funnel-planner-shell="wizard"] {
          max-width: min(94vw, 176rem) !important;
        }

        [data-funnel-planner-game-grid="true"] {
          display: grid;
          grid-template-columns: minmax(34rem, 1fr) minmax(28rem, 42rem);
          gap: clamp(1rem, 1.8vw, 2rem);
          align-items: start;
        }

        body[data-device-shell="4k"] [data-funnel-planner-game-grid="true"] {
          grid-template-columns: minmax(48rem, 1fr) minmax(58rem, 1fr);
        }

        @media (max-width: 1279px) {
          [data-funnel-planner-game-grid="true"] {
            grid-template-columns: minmax(0, 1fr);
          }
        }

        [data-funnel-planner-control-column="true"] {
          min-width: 0;
        }

        .funnel-planner-game-preview {
          min-width: 0;
          height: min(62vh, 42rem);
          border: 1px solid rgba(96, 165, 250, 0.34);
          border-radius: 1rem;
          overflow: hidden;
          background: #070b1c;
          box-shadow: 0 2rem 6rem rgba(0, 0, 0, 0.34);
          position: sticky;
          top: 5rem;
        }

        body[data-device-shell="4k"] .funnel-planner-game-preview {
          height: min(74vh, 58rem);
        }

        @media (max-width: 1279px) {
          .funnel-planner-game-preview {
            position: static;
            height: min(56vh, 32rem);
          }
        }

        .funnel-planner-game-preview__toolbar {
          position: absolute;
          top: 0.75rem;
          inset-inline-end: 0.75rem;
          z-index: 2;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.35rem;
          border: 1px solid rgba(125, 182, 255, 0.22);
          border-radius: 999px;
          background: rgba(7, 15, 31, 0.72);
          color: #dbeafe;
          font-size: 0.72rem;
          font-weight: 750;
          box-shadow: 0 0.75rem 2rem rgba(0, 0, 0, 0.24);
        }

        .funnel-planner-game-preview__toolbar span {
          padding-inline-start: 0.35rem;
        }

        .funnel-planner-game-preview__toolbar button {
          min-height: 1.6rem;
          border: 1px solid rgba(147, 197, 253, 0.36);
          border-radius: 999px;
          background: rgba(30, 64, 175, 0.42);
          color: #eff6ff;
          font-size: 0.7rem;
          font-weight: 800;
          padding: 0.2rem 0.55rem;
          cursor: pointer;
        }

        .funnel-planner-game-preview iframe {
          width: 100%;
          height: 100%;
          border: 0;
          display: block;
          background: transparent;
        }
      `}</style>
    </aside>
  );
}
